/**
 * 資料 (documents) の hooks。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §12
 */
import type {
  Document,
  DocumentPdfFile,
  DocumentSection,
  DocumentStatus,
  DocumentTheme,
  DocumentThankYouPage,
  PublicDocumentView,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

import { db, functions, storage } from '@/lib/firebase';

export interface DocumentRecord extends Document {
  id: string;
}

export function useDocuments() {
  return useQuery({
    queryKey: ['documents'],
    queryFn: async (): Promise<DocumentRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'documents'), orderBy('updatedAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Document) }));
    },
  });
}

export function useDocument(id: string | null | undefined) {
  return useQuery({
    queryKey: ['documents', 'detail', id],
    enabled: !!id,
    queryFn: async (): Promise<DocumentRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, 'documents', id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Document) };
    },
  });
}

const upsertDocumentFn = httpsCallable<
  {
    id?: string;
    title: string;
    slug?: string;
    primaryCategoryId?: string | null;
    status: DocumentStatus;
    theme?: DocumentTheme | null;
    sections: DocumentSection[];
    password?: string | null;
    validFrom?: string | null;
    validUntil?: string | null;
    autoReplyTemplate?: string | null;
    /** v3.2.24+: 閲覧認証方式 */
    authMode?: 'open' | 'password' | 'token';
    /** v3.2.24+: token モード時のリンク有効時間 (時間) */
    tokenValidityHours?: number | null;
    /** v3.2.28+: SA ポータル表示可否 */
    visibleToSa?: boolean;
  },
  { id: string; slug: string }
>(functions, 'upsertDocument');

export function useUpsertDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof upsertDocumentFn>[0]) => {
      const r = await upsertDocumentFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

const deleteDocumentFn = httpsCallable<{ id: string }, { id: string }>(
  functions,
  'deleteDocument',
);

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await deleteDocumentFn({ id });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

const generateDocumentFn = httpsCallable<
  {
    documentId: string;
    prompt: string;
    referenceUrl?: string;
    referenceKeywords?: string;
    pdfBase64?: string;
    pdfName?: string;
  },
  { documentId: string; sectionCount: number }
>(functions, 'generateDocument', { timeout: 540_000 });

export function useGenerateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof generateDocumentFn>[0]) => {
      const r = await generateDocumentFn(input);
      return r.data;
    },
    onSuccess: (_, input) => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      if (input?.documentId) {
        qc.invalidateQueries({
          queryKey: ['documents', 'detail', input.documentId],
        });
      }
    },
  });
}

const optimizeDocumentSectionFn = httpsCallable<
  {
    documentId: string;
    sectionId: string;
    prompt: string;
    referenceUrl?: string;
    referenceKeywords?: string;
    pdfBase64?: string;
    pdfName?: string;
  },
  { documentId: string; sectionId: string; config: Record<string, unknown> }
>(functions, 'optimizeDocumentSection', { timeout: 180_000 });

export function useOptimizeDocumentSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof optimizeDocumentSectionFn>[0]) => {
      const r = await optimizeDocumentSectionFn(input);
      return r.data;
    },
    onSuccess: (_, input) => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      if (input?.documentId) {
        qc.invalidateQueries({
          queryKey: ['documents', 'detail', input.documentId],
        });
      }
    },
  });
}

const getPublicDocumentFn = httpsCallable<
  { slug: string; password?: string | null },
  | { status: 'ok'; document: PublicDocumentView }
  | { status: 'password_required' }
  | { status: 'wrong_password' }
  | { status: 'expired' }
  | { status: 'not_found' }
>(functions, 'getPublicDocument');

export async function fetchPublicDocument(
  slug: string,
  password?: string | null,
) {
  const r = await getPublicDocumentFn({ slug, password: password ?? null });
  return r.data;
}

// ─────────────────────────────────────────────
// v3.2.28+: PDF ダウンロード型資料の hooks
// ─────────────────────────────────────────────

const createPdfDocumentFn = httpsCallable<
  {
    title: string;
    slug?: string;
    primaryCategoryId?: string | null;
    tokenValidityHours?: number | null;
    autoReplyTemplate?: string | null;
    /** v3.2.28+: SA ポータル表示可否 (既定: 表示) */
    visibleToSa?: boolean;
  },
  { id: string; slug: string }
>(functions, 'createPdfDocument');

export function useCreatePdfDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof createPdfDocumentFn>[0]) => {
      const r = await createPdfDocumentFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

const updatePdfDocumentMetaFn = httpsCallable<
  {
    id: string;
    title?: string;
    slug?: string;
    primaryCategoryId?: string | null;
    status?: DocumentStatus;
    tokenValidityHours?: number | null;
    autoReplyTemplate?: string | null;
    validUntil?: string | null;
    thankYouPage?: DocumentThankYouPage | null;
    /** v3.2.28+: SA ポータル表示可否 */
    visibleToSa?: boolean;
  },
  { ok: true }
>(functions, 'updatePdfDocumentMeta');

export function useUpdatePdfDocumentMeta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof updatePdfDocumentMetaFn>[0]) => {
      const r = await updatePdfDocumentMetaFn(input);
      return r.data;
    },
    onSuccess: (_, input) => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      if (input?.id) {
        qc.invalidateQueries({ queryKey: ['documents', 'detail', input.id] });
      }
    },
  });
}

const registerPdfDocumentFileFn = httpsCallable<
  {
    documentId: string;
    storagePath: string;
    fileName: string;
    size: number;
  },
  { ok: true; pdfFile: DocumentPdfFile }
>(functions, 'registerPdfDocumentFile', { timeout: 60_000 });

/**
 * PDF を Storage にアップロードし、その後 Firestore に登録する。
 * onProgress で進捗率 (0-100) を受け取れる。
 */
export async function uploadPdfDocumentFile(
  documentId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<DocumentPdfFile> {
  if (!file.name.toLowerCase().endsWith('.pdf')) {
    throw new Error('PDF ファイルのみアップロード可能です');
  }
  if (file.size > 50 * 1024 * 1024) {
    throw new Error('ファイルサイズは 50MB 以下にしてください');
  }
  const safeName = file.name.replace(/[^\w.\-]+/g, '_');
  const ts = Date.now();
  const storagePath = `documents/files/${documentId}/${ts}_${safeName}`;
  const ref = storageRef(storage, storagePath);
  const task = uploadBytesResumable(ref, file, { contentType: 'application/pdf' });

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        if (onProgress && snap.totalBytes > 0) {
          onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
        }
      },
      reject,
      () => resolve(),
    );
  });

  const r = await registerPdfDocumentFileFn({
    documentId,
    storagePath,
    fileName: file.name,
    size: file.size,
  });
  return r.data.pdfFile;
}

export function useUploadPdfDocumentFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      documentId: string;
      file: File;
      onProgress?: (pct: number) => void;
    }) => uploadPdfDocumentFile(input.documentId, input.file, input.onProgress),
    onSuccess: (_, input) => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['documents', 'detail', input.documentId] });
    },
  });
}

export interface PdfDownloadLinkInfo {
  tokenId: string;
  email: string;
  contactId: string | null;
  issuedAt: string;
  expiresAt: string | null;
  expired: boolean;
  url: string;
}

const listPdfDownloadLinksFn = httpsCallable<
  { documentId: string; baseUrl?: string | null },
  { ok: true; links: PdfDownloadLinkInfo[] }
>(functions, 'listPdfDownloadLinks');

/** ブラウザ実行時のみ origin を取得 (SSR / テスト時は undefined) */
function currentOrigin(): string | null {
  return typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : null;
}

export function usePdfDownloadLinks(documentId: string | null | undefined) {
  return useQuery({
    queryKey: ['documents', 'pdf-links', documentId],
    enabled: !!documentId,
    queryFn: async (): Promise<PdfDownloadLinkInfo[]> => {
      if (!documentId) return [];
      const r = await listPdfDownloadLinksFn({
        documentId,
        baseUrl: currentOrigin(),
      });
      return r.data.links;
    },
  });
}

const issuePdfDownloadLinkFn = httpsCallable<
  {
    documentId: string;
    email?: string | null;
    validityHours?: number | null;
    baseUrl?: string | null;
  },
  { ok: true; tokenId: string; url: string; expiresAt: string | null }
>(functions, 'issuePdfDownloadLink');

export function useIssuePdfDownloadLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: NonNullable<Parameters<typeof issuePdfDownloadLinkFn>[0]>,
    ) => {
      const r = await issuePdfDownloadLinkFn({
        documentId: input.documentId,
        email: input.email ?? null,
        validityHours: input.validityHours ?? null,
        baseUrl: input.baseUrl ?? currentOrigin(),
      });
      return r.data;
    },
    onSuccess: (_, input) => {
      if (input?.documentId) {
        qc.invalidateQueries({ queryKey: ['documents', 'pdf-links', input.documentId] });
      }
      // SA ポータル用「自分のアクティブトークン」キャッシュも無効化 → ボタン表示が即更新
      qc.invalidateQueries({ queryKey: ['documents', 'my-active-pdf-tokens'] });
    },
  });
}

const revokePdfDownloadLinkFn = httpsCallable<{ tokenId: string }, { ok: true }>(
  functions,
  'revokePdfDownloadLink',
);

export function useRevokePdfDownloadLink(documentId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tokenId: string) => {
      const r = await revokePdfDownloadLinkFn({ tokenId });
      return r.data;
    },
    onSuccess: () => {
      if (documentId) {
        qc.invalidateQueries({ queryKey: ['documents', 'pdf-links', documentId] });
      }
    },
  });
}

const extendPdfDownloadLinkFn = httpsCallable<
  { tokenId: string; validityHours: number | null },
  { ok: true; expiresAt: string | null }
>(functions, 'extendPdfDownloadLink');

export function useExtendPdfDownloadLink(documentId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof extendPdfDownloadLinkFn>[0]) => {
      const r = await extendPdfDownloadLinkFn(input);
      return r.data;
    },
    onSuccess: () => {
      if (documentId) {
        qc.invalidateQueries({ queryKey: ['documents', 'pdf-links', documentId] });
      }
    },
  });
}

// ─────────────────────────────────────────────
// listMyActivePdfTokens — 現在のユーザー自身が発行した有効トークンを取得
// (SaDocumentsPage で「URL コピー」か「DL リンク発行」かを判定するため)
// ─────────────────────────────────────────────
export interface MyActivePdfTokenInfo {
  documentId: string;
  tokenId: string;
  url: string;
  issuedAt: string;
  expiresAt: string | null;
}

const listMyActivePdfTokensFn = httpsCallable<
  { baseUrl?: string | null },
  { ok: true; tokensByDocId: Record<string, MyActivePdfTokenInfo> }
>(functions, 'listMyActivePdfTokens');

export function useMyActivePdfTokens(enabled: boolean = true) {
  return useQuery({
    queryKey: ['documents', 'my-active-pdf-tokens'],
    enabled,
    queryFn: async (): Promise<Record<string, MyActivePdfTokenInfo>> => {
      const r = await listMyActivePdfTokensFn({ baseUrl: currentOrigin() });
      return r.data.tokensByDocId;
    },
  });
}

const getPdfDownloadUrlFn = httpsCallable<
  { slug: string; accessToken: string },
  | {
      status: 'ok';
      downloadUrl: string;
      fileName: string;
      size: number;
      expiresAt: string;
    }
  | { status: 'not_found' }
  | { status: 'token_required' }
  | { status: 'token_invalid' }
  | { status: 'expired' }
  | { status: 'file_missing' }
>(functions, 'getPdfDownloadUrl');

export async function fetchPdfDownloadUrl(slug: string, accessToken: string) {
  const r = await getPdfDownloadUrlFn({ slug, accessToken });
  return r.data;
}
